import { User, Bell, Menu, X } from "lucide-react";
import { useTelegram } from "@/contexts/TelegramContext";
import { useAuth } from "@/contexts/AuthContext";
import { useHeader } from "@/contexts/HeaderContext";
import { useNavigate, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from "@/components/ui/sheet";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { roleMenus, pickLocale } from "@/lib/roleMenus";
import type { LocaleCode } from "@/lib/roleMenus";
import {
  Home,
  Package,
  Users,
  Settings as SettingsIcon,
  Ticket,
  MessageCircle,
  Newspaper,
  LucideProps,
} from "lucide-react";

/**
 * PrimaryHeader Component - Global header for all pages
 * Displays language switcher, notifications, achievements, and user info
 * On mobile (<768px): Shows hamburger menu with navigation Sheet
 */
interface PrimaryHeaderProps {
  className?: string;
}

export const PrimaryHeader = ({ className = "" }: PrimaryHeaderProps) => {
  const { user: tgUser, isInTelegram, hapticFeedback } = useTelegram();
  const { user } = useAuth();
  const { headerConfig } = useHeader();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleProfileClick = () => {
    navigate("/profile");
  };

  const handleNotificationClick = () => {
    navigate("/news");
  };

  const handleUserClick = () => {
    if (isInTelegram && hapticFeedback) {
      hapticFeedback("light");
    }
    handleProfileClick();
  };

  const hasUnreadNotifications = false;

  // Navigation menu configuration
  type IconType = React.ComponentType<LucideProps>;
  const iconMap: Record<string, IconType> = {
    LayoutDashboard: Home,
    Users,
    Package,
    Settings: SettingsIcon,
    Star: Package,
    UserCircle: Users,
    FileChartColumn: Package,
    Ticket,
    MessageCircle,
    Newspaper,
  };

  const currentRole = user?.user_metadata?.role || "buyer";
  const locale: LocaleCode = pickLocale(
    i18n.language ||
      (typeof navigator !== "undefined" ? navigator.language : "en"),
  );
  const menuItems =
    roleMenus[currentRole as keyof typeof roleMenus]?.menu || [];

  const handleNavClick = () => {
    setMobileMenuOpen(false);
    if (isInTelegram && hapticFeedback) {
      hapticFeedback("light");
    }
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b border-gray-200 bg-gray-50/95 backdrop-blur shadow-sm supports-[backdrop-filter]:bg-gray-50/80 ${className}`}
    >
      <div
        className="container flex h-18 items-center"
        style={{ padding: "16px" }}
      >
        {/* Mobile Menu Button (only on mobile) */}
        {isMobile && (
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                className="md:hidden h-11 w-11 p-0 hover:bg-gray-200/70 text-gray-700"
                aria-label="Open menu"
              >
                <Menu className="h-7 w-7" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="left"
              className="w-[280px] sm:w-[320px] bg-surface"
            >
              <SheetHeader className="mb-6">
                <SheetTitle className="flex items-center gap-3">
                  <img
                    src="/trafomatic-logo.svg"
                    alt="Trafomatic"
                    className="h-8 w-8"
                    style={{ objectFit: "contain" }}
                  />
                  <span className="text-lg font-semibold">Trafomatic</span>
                </SheetTitle>
              </SheetHeader>

              {/* Mobile Navigation Menu */}
              <nav className="flex flex-col gap-2 mt-4">
                {menuItems.map(({ route, label, icon }) => {
                  const Icon = iconMap[icon] || Home;
                  const localized = label[locale];
                  return (
                    <NavLink
                      key={route}
                      to={route}
                      onClick={handleNavClick}
                      className={({ isActive }) => `
                        flex items-center gap-3 px-4 py-3 rounded-lg 
                        transition-all duration-200 text-base
                        ${
                          isActive
                            ? "bg-accent text-accent-foreground font-semibold"
                            : "text-text-primary hover:bg-hover"
                        }
                      `}
                    >
                      <Icon className="h-6 w-6 sm:h-7 sm:w-7 flex-shrink-0" />
                      <span>{localized}</span>
                    </NavLink>
                  );
                })}
              </nav>
            </SheetContent>
          </Sheet>
        )}

        {/* Обертка для правых иконок с ml-auto */}
        <div className="ml-auto flex items-center gap-2">
          <div
            className="flex items-center gap-2 overflow-hidden"
            style={{ gap: "8px" }}
          >
            {/* Language Switcher */}
            <LanguageSwitcher size="lg" />

            {/* Notifications Button */}
            {user && (
              <Button
                variant="ghost"
                onClick={handleNotificationClick}
                className="relative hover:bg-gray-200/70 text-gray-700 transition-colors duration-200 h-11 w-11 p-0"
                title="Notifications"
              >
                <Bell className="h-7 w-7" />
                {hasUnreadNotifications && (
                  <div
                    className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center bg-primary text-primary-foreground text-xs"
                    style={{ borderRadius: "9999px", fontSize: "12px" }}
                  >
                    1
                  </div>
                )}
              </Button>
            )}

            {/* User Info */}
            {user && (
              <div
                className="flex items-center flex-shrink-0"
                style={{ gap: "8px" }}
              >
                <Button
                  variant="ghost"
                  onClick={handleUserClick}
                  className="flex items-center hover:bg-gray-200/70 text-gray-700 transition-colors duration-200 h-11 w-11 p-0"
                  style={{ gap: "8px" }}
                >
                  <User className="h-7 w-7" />
                  {tgUser && (
                    <span
                      className="hidden sm:block text-gray-600 truncate max-w-20"
                      style={{ fontSize: "14px" }}
                    >
                      {tgUser.firstName}
                    </span>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dynamic content for mobile */}
      {isMobile && headerConfig.title && (
        <div className="container flex h-12 items-center justify-between pb-2">
          <h1 className="text-2xl font-bold text-gray-900">
            {headerConfig.title}
          </h1>
          <div>{headerConfig.actions}</div>
        </div>
      )}
    </header>
  );
};

export default PrimaryHeader;
